import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiShoppingBag, FiUsers, FiShield, FiTruck, FiStar } from 'react-icons/fi';
import ProductCard from '../components/product/ProductCard';
import FlashDeals from '../components/deals/FlashDeals';
import ProductRecommendations from '../components/product/ProductRecommendations';
import productService from '../services/product.service';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import heroBanner from '../assets/hero-banner.png';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLanguage();
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        setLoading(true);
        const res = await productService.getAll({ limit: 8, sort: '-createdAt' });
        const products = res.data?.data || res.data?.products || res.data || [];
        setFeaturedProducts(Array.isArray(products) ? products : []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, [lang]);

  const PRODUCE_CATEGORIES = [
    { name: 'Dairy', icon: '🥛', path: '/products?category=dairy', color: '#DBEAFE' },
    { name: 'Vegetables', icon: '🥬', path: '/products?category=vegetables', color: '#DCFCE7' },
    { name: 'Fruits', icon: '🍎', path: '/products?category=fruits', color: '#FEF3C7' },
    { name: 'Flowers', icon: '🌺', path: '/products?category=flowers', color: '#FCE7F3' },
    { name: 'Grains', icon: '🌾', path: '/products?category=grains', color: '#FED7AA' },
    { name: 'Food Products', icon: '🍯', path: '/products?category=food_products', color: '#E0E7FF' },
  ];

  const BUYER_TYPES = [
    { icon: '🏠', title: 'Individual Buyers', desc: 'Fresh produce delivered to your home. No minimum order.' },
    { icon: '🏪', title: 'Retailers', desc: 'Special retailer pricing with regular supply for your shop.' },
    { icon: '🏭', title: 'Wholesale Buyers', desc: 'Bulk quantities at best prices. Direct from the farm.' },
  ];

  return (
    <>
      {/* Hero */}
      <section style={{
        position: 'relative',
        minHeight: isMobile ? '60vh' : '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${heroBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(6, 78, 59, 0.95) 0%, rgba(6, 78, 59, 0.88) 35%, rgba(6, 78, 59, 0.55) 60%, rgba(6, 78, 59, 0.15) 100%)',
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 2, padding: '4rem 0' }}>
          <div className="row">
            <div className="col-lg-7 col-md-8">
              <p style={{
                color: '#EAB308', fontWeight: 700, fontSize: '14px', letterSpacing: '2px',
                textTransform: 'uppercase', marginBottom: '12px',
              }}>
                🌾 From Soil to Doorstep • Fair. Fresh. Direct.
              </p>

              <h1 style={{
                color: '#FFF', fontWeight: 900, fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
                lineHeight: 1.1, marginBottom: '20px',
              }}>
                Buy Directly from{' '}
                <span style={{ color: '#EAB308' }}>Local Farmers</span>
                <br />Dairy, Vegetables, Fruits & More
              </h1>

              <p style={{
                color: 'rgba(255,255,255,0.85)', fontSize: '1.15rem',
                maxWidth: '520px', lineHeight: 1.7, marginBottom: '32px',
              }}>
                FarmDirectly connects you with verified farmers for the freshest dairy, vegetables, fruits, flowers and food products — delivered straight to your doorstep. Wholesale, retail or individual.
              </p>

              <div className="d-flex flex-wrap gap-3">
                <Link to="/products" className="btn btn-lg px-4 py-2" style={{
                  background: '#EAB308', color: '#064E3B', fontWeight: 700,
                  border: 'none', borderRadius: '8px', fontSize: '1rem',
                }}>
                  Shop Farm Fresh <FiArrowRight className="ms-2" />
                </Link>
                <Link to="/register?role=farmer" className="btn btn-lg px-4 py-2" style={{
                  background: 'transparent', color: '#FFF', fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.5)', borderRadius: '8px', fontSize: '1rem',
                }}>
                  🌱 Sell Your Produce
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Produce Categories */}
      <section className="section-padding" style={{ background: '#FFF' }}>
        <div className="container">
          <h2 className="text-center fw-bold mb-2">🌿 Farm Fresh Categories</h2>
          <p className="text-center text-muted mb-4">Sourced directly from verified farmers across India</p>
          <div className="row g-3 justify-content-center">
            {PRODUCE_CATEGORIES.map((cat, i) => (
              <div key={i} className="col-6 col-md-4 col-lg-2">
                <Link to={cat.path} className="text-decoration-none">
                  <div className="text-center p-3 rounded-3 h-100" style={{
                    background: cat.color, transition: 'transform 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>{cat.icon}</div>
                    <div className="fw-semibold" style={{ color: '#171717' }}>{cat.name}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Buyer Types */}
      <section className="section-padding" style={{ background: '#F0FDF4' }}>
        <div className="container">
          <h2 className="text-center fw-bold mb-4">We Serve All Buyers</h2>
          <div className="row g-4">
            {BUYER_TYPES.map((bt, i) => (
              <div key={i} className="col-md-4">
                <div className="bg-white p-4 rounded-3 h-100 shadow-sm text-center">
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>{bt.icon}</div>
                  <h5 className="fw-bold">{bt.title}</h5>
                  <p className="text-muted mb-0">{bt.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flash Deals */}
      <FlashDeals />

      {/* Featured Products */}
      <section className="section-padding">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="fw-bold mb-0">🌾 Fresh From the Farm</h2>
            <Link to="/products" className="btn btn-fd-outline btn-sm">
              View All <FiArrowRight className="ms-1" />
            </Link>
          </div>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-success" /></div>
          ) : featuredProducts.length > 0 ? (
            <div className="row g-3">
              {featuredProducts.map(p => (
                <div key={p._id || p.id} className="col-6 col-md-4 col-lg-3">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <p className="fs-5">🌱 Fresh produce coming soon!</p>
              <p>Farmers are adding their products. Check back shortly.</p>
            </div>
          )}
        </div>
      </section>

      {/* Recommendations */}
      <ProductRecommendations />

      {/* Trust / Why FarmDirectly */}
      <section className="section-padding" style={{ background: '#064E3B', color: '#FFF' }}>
        <div className="container">
          <h2 className="text-center fw-bold mb-5" style={{ color: '#EAB308' }}>Why FarmDirectly?</h2>
          <div className="row g-4 text-center">
            {[
              { icon: <FiStar size={28} />, title: 'Farm Fresh Quality', desc: 'Harvested and delivered within 24–48 hours. No cold storage. No middlemen.' },
              { icon: <FiShield size={28} />, title: 'Verified Farmers', desc: 'Every farmer is verified with KYC, land documents, and produce quality checks.' },
              { icon: <FiTruck size={28} />, title: 'Doorstep Delivery', desc: 'Reliable delivery to your home, shop or warehouse across India.' },
              { icon: <FiUsers size={28} />, title: 'Fair Prices for All', desc: 'Farmers get better margins. Buyers get fresher produce at fair prices.' },
            ].map((item, i) => (
              <div key={i} className="col-6 col-md-3">
                <div style={{ color: '#EAB308', marginBottom: '12px' }}>{item.icon}</div>
                <h6 className="fw-bold">{item.title}</h6>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
